import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClockIcon } from 'lucide-react';
const WINDOW_MS = 9 * 60 * 60 * 1000;
const STORAGE_KEY = 'bjj_instructor_ready_countdown_target';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + WINDOW_MS;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + WINDOW_MS;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(WINDOW_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + WINDOW_MS;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(WINDOW_MS);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = WINDOW_MS - remaining;
  const progress = Math.min(95, 81 + elapsed / WINDOW_MS * 14);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-navy-deep border border-gold/50 rounded-2xl p-6 md:p-8 shadow-lg text-white" data-id="element-61" data-mp="2ih0pwos83fph">
      <div className="text-center" data-id="element-62" data-mp="2nbjup94u8xcz">
        <div className="flex items-center justify-center gap-2 text-gold-light font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-63" data-mp="2jbjhfrth6s7i">
          <AlarmClockIcon size={20} className="animate-pulse" data-id="element-64" data-mp="2ecggaigm964t" />
          <span data-id="element-65" data-mp="29e51dtbgs5fq">⏱️ The launch price ends soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-66" data-mp="24n56supthzge">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-67" data-mp="24l271iyv4ryh" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-68" data-mp="1znahgvloxhxg">·</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-69" data-mp="1vmqirnlgy697" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-70" data-mp="1vo1o3rukbm2f">·</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-71" data-mp="1qmcaq5jvawb6" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-white/80 mb-8" data-id="element-72" data-mp="1lokj7cn4xc7i">
          After this, the price goes up and it does not come back down.
        </p>
        <div className="space-y-2" data-id="element-73" data-mp="18mdpl0d7ad2">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-74" data-mp="64jxk1au6yul">
            <span className="text-white/70" data-id="element-75" data-mp="b5hc83wn1arc">Launch price spots remaining</span>
            <span className="text-gold-light" data-id="element-76" data-mp="f6jfcvtoe6yl">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden border border-gold/30" data-id="element-77" data-mp="3el4zwz8yqtoq">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gold rounded-full" data-id="element-78" data-mp="3jjzpql5cliic" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-white/70 font-medium" data-id="element-79" data-mp="2vuwxykh029a4">
            {Math.round(progress)}% of spots have been claimed
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-white/5 border-2 border-gold/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-80" data-mp="2bskdli538vh6">
      <div className="flex items-baseline justify-center gap-1" data-id="element-81" data-mp="2qhotgsoeunm4">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-82" data-mp="2qj9mgdv2tyhv">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-gold-light" data-id="element-83" data-mp="2zhv5sb8dhhjp">
          {label}
        </span>
      </div>
    </div>;
}